import { oidcRedirectPath, sidebarCollapsedStorageKey } from "./config.js";
import type {
  AccessDeniedState,
  AuthState,
  AuthStatus,
  DialogView,
  LoadingState,
  NoAppsState,
  NavEntry,
  NotificationToast,
  RegistryEntry,
  SelectionState,
  ShellScreen,
  TenantSummary,
  Workspace
} from "./types";

export interface ShellState {
  shellReady: boolean;
  auth: AuthState;
  screen: ShellScreen;
  accessibleTenants: TenantSummary[];
  tenantSelection: SelectionState;
  loading: LoadingState;
  accessDenied: AccessDeniedState;
  noApps: NoAppsState;
  registry: RegistryEntry[];
  currentEntry: RegistryEntry | null;
  navItems: NavEntry[];
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  expandedAppId: string | null;
  notifications: NotificationToast[];
  dialogs: DialogView[];
  sidebarCollapsed: boolean;
  identityEmail: string | null;
}

export type ShellAction =
  | { type: "shell-ready" }
  | { type: "set-auth"; status: AuthStatus; message: string }
  | { type: "set-screen"; screen: ShellScreen }
  | { type: "set-accessible-tenants"; tenants: TenantSummary[] }
  | { type: "set-tenant-selection"; selection: SelectionState }
  | { type: "set-loading"; loading: LoadingState }
  | { type: "set-access-denied"; accessDenied: AccessDeniedState }
  | { type: "set-no-apps"; noApps: NoAppsState }
  | { type: "set-registry"; registry: RegistryEntry[] }
  | { type: "set-current-entry"; entry: RegistryEntry | null }
  | { type: "set-identity-email"; email: string | null }
  | { type: "nav-register"; entry: NavEntry }
  | { type: "nav-unregister"; id: string }
  | { type: "nav-clear" }
  | { type: "set-workspaces"; workspaces: Workspace[] }
  | { type: "set-active-workspace"; workspaceId: string | null }
  | { type: "set-expanded-app"; appId: string | null }
  | { type: "notification-add"; notification: NotificationToast }
  | { type: "notification-remove"; id: string }
  | { type: "dialog-add"; dialog: DialogView }
  | { type: "dialog-remove"; id: string }
  | { type: "toggle-sidebar"; collapsed: boolean };

export function createInitialState(): ShellState {
  const isAuthCallbackPath = normalizePathname(window.location.pathname) === oidcRedirectPath;

  return {
    shellReady: false,
    auth: isAuthCallbackPath
      ? { status: "callback", message: "Completing sign-in." }
      : { status: "loading", message: "Checking your session." },
    screen: "auth",
    accessibleTenants: [],
    tenantSelection: {
      message: "Choose a tenant to continue.",
      loadingTenantId: null,
      requestedAppPath: "/"
    },
    loading: {
      title: "Loading tenant",
      message: "Preparing your workspace."
    },
    accessDenied: {
      title: "Access denied",
      message: "You do not have access to the requested tenant."
    },
    noApps: {
      title: "No applications available",
      message: "This tenant does not have an application assigned to its first workspace."
    },
    registry: [],
    currentEntry: null,
    navItems: [],
    workspaces: [],
    activeWorkspaceId: null,
    expandedAppId: null,
    notifications: [],
    dialogs: [],
    sidebarCollapsed: localStorage.getItem(sidebarCollapsedStorageKey) === "true",
    identityEmail: null
  };
}

function normalizePathname(pathname: string): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;

  return normalized === "/" ? normalized : normalized.replace(/\/+$/u, "");
}

export function shellReducer(state: ShellState, action: ShellAction): ShellState {
  switch (action.type) {
    case "shell-ready":
      return state.shellReady ? state : { ...state, shellReady: true };
    case "set-auth":
      return { ...state, auth: { status: action.status, message: action.message } };
    case "set-screen":
      return { ...state, screen: action.screen };
    case "set-accessible-tenants":
      return { ...state, accessibleTenants: action.tenants };
    case "set-tenant-selection":
      return { ...state, tenantSelection: action.selection };
    case "set-loading":
      return { ...state, loading: action.loading };
    case "set-access-denied":
      return { ...state, accessDenied: action.accessDenied };
    case "set-no-apps":
      return { ...state, noApps: action.noApps };
    case "set-registry":
      return { ...state, registry: action.registry };
    case "set-current-entry": {
      // A manually-expanded workspace-nav section (from clicking a different
      // app's collapsed header to preview it) only makes sense until the active
      // app actually changes — once it does, the accordion should snap back to
      // following whichever app just became active, not stay pinned on
      // whatever was last clicked open. Same-app sub-route navigation (the
      // entry id is unchanged) leaves it alone.
      const appChanged = action.entry?.id !== state.currentEntry?.id;
      return {
        ...state,
        currentEntry: action.entry,
        expandedAppId: appChanged ? null : state.expandedAppId
      };
    }
    case "set-identity-email":
      return { ...state, identityEmail: action.email };
    case "nav-register":
      return { ...state, navItems: registerNavItem(state.navItems, action.entry) };
    case "nav-unregister":
      return { ...state, navItems: state.navItems.filter((entry) => entry.id !== action.id) };
    case "nav-clear":
      return state.navItems.length === 0 ? state : { ...state, navItems: [] };
    case "set-workspaces":
      return { ...state, workspaces: action.workspaces };
    case "set-active-workspace":
      return action.workspaceId === state.activeWorkspaceId
        ? state
        : { ...state, activeWorkspaceId: action.workspaceId, expandedAppId: null };
    case "set-expanded-app":
      return { ...state, expandedAppId: action.appId };
    case "notification-add":
      return { ...state, notifications: [action.notification, ...state.notifications] };
    case "notification-remove":
      return {
        ...state,
        notifications: state.notifications.filter((entry) => entry.id !== action.id)
      };
    case "dialog-add":
      return { ...state, dialogs: [...state.dialogs, action.dialog] };
    case "dialog-remove":
      return { ...state, dialogs: state.dialogs.filter((entry) => entry.id !== action.id) };
    case "toggle-sidebar":
      return { ...state, sidebarCollapsed: action.collapsed };
    default:
      return state;
  }
}

function registerNavItem(navItems: NavEntry[], entry: NavEntry): NavEntry[] {
  const exists = navItems.some((item) => item.id === entry.id);

  return exists
    ? navItems.map((item) => (item.id === entry.id ? entry : item))
    : [...navItems, entry];
}

export function getDebugNavItems(state: ShellState): NavEntry[] {
  const entries = state.navItems;
  const currentEntry = state.currentEntry;

  if (!currentEntry) {
    return entries;
  }

  const currentAppId = currentEntry.id;
  const homeAliases = entries.flatMap((entry) =>
    entry.id === `${currentAppId}:home` ? [{ ...entry, id: currentAppId }] : []
  );

  return [...entries, ...homeAliases];
}
